import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { selectUserRoles } from '../../features/auth/authSlice';
import { useListAuditLogsQuery } from '../../features/audit/auditApi';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EmptyState from '../../components/ui/EmptyState';
import { MobileCard, DataRow } from '../../components/ui/MobileCard';
import { SkeletonTable } from '../../components/ui/Skeleton';
import CauserAutocomplete from '../../components/admin/CauserAutocomplete';
import { ClockIcon, ChevronDownIcon, ShieldIcon } from '../../components/ui/icons';
import { authErrorMessage } from '../../lib/authErrorMessage';
import {
  KNOWN_LOG_NAMES,
  logNameMeta,
  mergeKnownValues,
  validateAuditDateRange,
  prettifyDescription,
} from '../../lib/validators/auditLogSchema';

const PER_PAGE = 15;

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function CauserChip({ causer }) {
  if (!causer) return <span className="text-sm text-foreground-subtle italic">System</span>;
  return (
    <div className="min-w-0">
      <p className="text-sm text-foreground truncate">{causer.full_name}</p>
      <p className="text-xs text-foreground-subtle truncate">{causer.roles?.[0] || 'user'}</p>
    </div>
  );
}

function PropertiesDetail({ properties }) {
  const entries = Object.entries(properties || {});
  if (entries.length === 0) {
    return <p className="text-xs text-foreground-subtle">No additional detail recorded for this entry.</p>;
  }
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
      {entries.map(([key, value]) => (
        <div key={key} className="text-xs">
          <dt className="text-foreground-subtle">{key}</dt>
          <dd className="text-foreground font-mono break-all mt-0.5">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One expandable row — clicking anywhere on the row toggles a detail strip
 * showing the raw event/causer email/properties, so the summary columns
 * don't need to try to cram everything in. `properties` is an open object
 * per the docs (no fixed shape), so the detail section is just a generic
 * key/value dump rather than assuming particular fields exist.
 */
function LogRow({ entry, expanded, onToggle }) {
  const meta = logNameMeta(entry.logName);
  return (
    <>
      <tr className="cursor-pointer hover:bg-background-elevated transition-colors" onClick={onToggle}>
        <td className="py-3 pr-4 text-foreground-muted whitespace-nowrap">{formatDateTime(entry.createdAt)}</td>
        <td className="py-3 pr-4">
          <Badge tone={meta.tone}>{meta.label}</Badge>
        </td>
        <td className="py-3 pr-4 text-foreground">{prettifyDescription(entry.description)}</td>
        <td className="py-3 pr-4">
          <CauserChip causer={entry.causer} />
        </td>
        <td className="py-3 pr-4 text-foreground-subtle whitespace-nowrap">
          {entry.subjectType ? `${entry.subjectType} #${entry.subjectId}` : '—'}
        </td>
        <td className="py-3 text-right">
          <ChevronDownIcon className={clsx('h-4 w-4 text-foreground-subtle transition-transform ml-auto', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-background-elevated/50">
          <td colSpan={6} className="px-4 py-4">
            <div className="space-y-2">
              <p className="text-xs text-foreground-subtle">
                Event: <span className="text-foreground">{entry.event || '—'}</span>
                {entry.causer && (
                  <>
                    {' '}
                    · Causer email: <span className="text-foreground">{entry.causer.email}</span>
                  </>
                )}
              </p>
              <PropertiesDetail properties={entry.properties} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * GET /admin/audit-logs — gated by the super-admin ROLE specifically, not
 * the manage-* permission pattern every other admin endpoint uses so far.
 * The route itself stays reachable by any admin/super-admin (see
 * AppRoutes.jsx) so a plain admin who lands here directly gets this page's
 * own "no permission" state below rather than being silently bounced to the
 * homepage — the sidebar link itself is only ever rendered for super-admins
 * (see Sidebar.jsx), so this in-page gate is the fallback for a stale
 * bookmark or typed-in URL, not the primary access control (the API
 * enforces the real one regardless).
 */
export default function AuditLog() {
  const roles = useSelector(selectUserRoles);
  const isSuperAdmin = roles.includes('super-admin');

  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const logName = searchParams.get('log_name') || '';
  const event = searchParams.get('event') || '';
  const causerUuid = searchParams.get('causer_uuid') || '';
  const search = searchParams.get('search') || '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const [searchDraft, setSearchDraft] = useState(search);
  const [dateError, setDateError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  // Debounce the free-text box into the URL/query rather than firing a
  // request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== search) updateParams({ search: searchDraft || undefined, page: undefined });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const { data, isLoading, isFetching, isError, error } = useListAuditLogsQuery(
    {
      from: from || undefined,
      to: to || undefined,
      log_name: logName || undefined,
      event: event || undefined,
      causer_uuid: causerUuid || undefined,
      search: search || undefined,
      per_page: PER_PAGE,
      page,
    },
    { skip: !isSuperAdmin }
  );

  const entries = data?.entries ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  // Every hook above this line runs unconditionally on every render — the
  // super-admin gate below is a plain conditional return of JSX, not a
  // conditional hook call, so it can't desync hook order between renders.
  const logNameOptions = useMemo(() => mergeKnownValues(KNOWN_LOG_NAMES, entries.map((e) => e.logName)), [entries]);
  const eventOptions = useMemo(() => Array.from(new Set(entries.map((e) => e.event).filter(Boolean))), [entries]);

  if (!isSuperAdmin) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="h-12 w-12 rounded-2xl bg-danger-soft text-danger flex items-center justify-center mx-auto mb-4">
          <ShieldIcon className="h-6 w-6" />
        </div>
        <h1 className="text-xl font-semibold text-foreground mb-2">You don't have permission to view this</h1>
        <p className="text-sm text-foreground-muted">
          Audit Logs is restricted to super-admin accounts. If you believe you should have access, contact a super-admin.
        </p>
      </div>
    );
  }

  const onDateChange = (key, value) => {
    const nextFrom = key === 'from' ? value : from;
    const nextTo = key === 'to' ? value : to;
    const err = validateAuditDateRange(nextFrom, nextTo);
    setDateError(err);
    if (err) return;
    updateParams({ [key]: value || undefined, page: undefined });
  };

  const clearFilters = () => {
    setSearchDraft('');
    setDateError(null);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = !!(from || to || logName || event || causerUuid || search);

  const goToPage = (newPage) => updateParams({ page: newPage <= 1 ? undefined : String(newPage) });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-2xl accent-gradient text-white flex items-center justify-center shrink-0 shadow-glow-sm">
          <ClockIcon className="h-5 w-5" />
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground">Audit logs</h1>
            <Badge tone="success">Live</Badge>
          </div>
          <p className="text-foreground-muted text-sm mt-0.5">Platform activity trail — super-admin only.</p>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onDateChange('from', e.target.value)}
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onDateChange('to', e.target.value)}
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Category</label>
            <select
              value={logName}
              onChange={(e) => updateParams({ log_name: e.target.value || undefined, page: undefined })}
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              <option value="">All categories</option>
              {logNameOptions.map((v) => (
                <option key={v} value={v}>
                  {logNameMeta(v).label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Event</label>
            <select
              value={event}
              onChange={(e) => updateParams({ event: e.target.value || undefined, page: undefined })}
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              <option value="">All events</option>
              {eventOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <CauserAutocomplete value={causerUuid} onChange={(uuid) => updateParams({ causer_uuid: uuid || undefined, page: undefined })} />
          <Input label="Search" placeholder="Search descriptions…" value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} />
        </div>

        {dateError && <p className="mt-3 text-sm text-danger">{dateError}</p>}

        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <p className="text-xs text-foreground-subtle">
              {isFetching ? 'Updating…' : `${meta?.total ?? 0} matching ${meta?.total === 1 ? 'entry' : 'entries'}`}
            </p>
            <button type="button" onClick={clearFilters} className="text-xs text-accent hover:underline">
              Clear filters
            </button>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Activity" subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined} />

        {isLoading && <SkeletonTable columns={['Timestamp', 'Category', 'Description', 'Causer', 'Subject', '']} />}

        {isError && (
          <p className="py-8 text-center text-danger">
            {authErrorMessage(error, {
              forbidden: "Your account doesn't have permission to view the audit log.",
              fallback: 'Could not load audit logs.',
            })}
          </p>
        )}

        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">Something went wrong loading this data. Check the console for details.</p>
        )}

        {!isLoading && !isError && !parseError && entries.length === 0 && (
          <EmptyState icon={ClockIcon} title="No activity found for these filters" body="Try widening the date range or clearing a filter." />
        )}

        {!isLoading && !isError && !parseError && entries.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-foreground-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">Timestamp</th>
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium">Description</th>
                    <th className="py-2 pr-4 font-medium">Causer</th>
                    <th className="py-2 pr-4 font-medium">Subject</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map((entry) => (
                    <LogRow
                      key={entry.id}
                      entry={entry}
                      expanded={expandedId === entry.id}
                      onToggle={() => setExpandedId((id) => (id === entry.id ? null : entry.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {entries.map((entry) => {
                const meta = logNameMeta(entry.logName);
                const expanded = expandedId === entry.id;
                return (
                  <MobileCard
                    key={entry.id}
                    className="cursor-pointer"
                    onClick={() => setExpandedId((id) => (id === entry.id ? null : entry.id))}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                      <span className="text-xs text-foreground-subtle">{formatDateTime(entry.createdAt)}</span>
                    </div>
                    <p className="text-sm text-foreground">{prettifyDescription(entry.description)}</p>
                    <DataRow label="Causer" value={entry.causer ? entry.causer.full_name : 'System'} />
                    <DataRow label="Subject" value={entry.subjectType ? `${entry.subjectType} #${entry.subjectId}` : '—'} />
                    {expanded && (
                      <div className="pt-2 mt-2 border-t border-border">
                        <PropertiesDetail properties={entry.properties} />
                      </div>
                    )}
                  </MobileCard>
                );
              })}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => goToPage(meta.currentPage - 1)}>
                    Previous
                  </Button>
                  <Button variant="ghost" size="sm" disabled={meta.currentPage >= meta.lastPage} onClick={() => goToPage(meta.currentPage + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
